import { malformed, readJson, request } from './request'

/**
 * The space the signed-in person works in.
 *
 * Which one it is, is never sent by the browser and never chosen here — the service works it out
 * from the session. This asks for it only so the frame can show which space the work is going into.
 */
export interface ActiveSpace {
  readonly id: string
  /**
   * What the space is called, when it is called anything.
   *
   * There is exactly one space per person today and it carries no name, so this is `null` and the
   * frame says "your space". A named space is the same field with something in it.
   */
  readonly name: string | null
}

const SPACE_ENDPOINT = '/api/space'

/**
 * Asks the service for the active space.
 *
 * A session that has run out comes back as a `refused` failure rather than as an error to show:
 * there is nothing wrong, and the only useful answer is the way back in.
 */
export async function fetchActiveSpace(signal?: AbortSignal): Promise<ActiveSpace> {
  const response = await request(SPACE_ENDPOINT, { signal })
  const payload = await readJson<Partial<ActiveSpace>>(response)

  if (typeof payload.id !== 'string') {
    throw malformed('The answer did not name a space.')
  }

  return { id: payload.id, name: typeof payload.name === 'string' ? payload.name : null }
}
